import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Input, Button, Select } from "antd";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  IdcardOutlined,
  PhoneOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  RegiterPatient,
  savePatientDetails,
} from "../Redux/Slice/Login/Register";

const { Option } = Select;

const nationalityOptions = [
  { label: "India", value: "India", code: "+91" },
  { label: "United States", value: "USA", code: "+1" },
  { label: "United Arab Emirates", value: "UAE", code: "+971" },
  { label: "United Kingdom", value: "UK", code: "+44" },
];

const genderOptions = ["Male", "Female", "Other"];
const visaTypeOptions = ["Resident", "Local", "Visitor"];
const maritalStatusOptions = [
  "Single",
  "Married",
  "Divorced",
  "Widowed",
  "Seperated",
  "Other",
];

const phonePlaceholders = {
  India: "e.g., +91 98765 43210",
  USA: "e.g., +1 987 654 3210",
  UAE: "e.g., +971 50 123 4567",
  UK: "e.g., +44 7700 900123",
};

const RegisterPatient = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [step, setStep] = useState("account");

  // Account setup
  const [regUserName, setRegUserName] = useState("");
  const [regPassword, setRegPassword] = useState("");

  const [deviceId, setDeviceId] = useState("UNKNOWN_DEVICE");

  useEffect(() => {
    const uniqueId = crypto.randomUUID(); // random unique id
    setDeviceId(uniqueId);
  }, []);

  // Patient details
  const [patientDetails, setPatientDetails] = useState({
    nationalId: "",
    name: "",
    dob: "",
    age: "",
    gender: "",
    maritalStatus: "",
    nationality: "",
    visaType: "",
    otherId: "",
    occupation: "",
    address: "",
    phoneNo: "",
    emailId: "",
  });

  const [countryCode, setCountryCode] = useState("");

  const { data, loading, error, dataSave, errorSave, loadingSave } =
    useSelector((state) => state.register);
  const status = data?.status;
  const message = data?.message;
  const token = data?.data?.token;

  const saveStatus = dataSave?.status;
  const saveMessage = dataSave?.message;
  // const user = dataSave?.data?.userDetails;

  useEffect(() => {
    if (status && token) {
      toast.success(message);

      localStorage.setItem("token", token);

      const userDetails = data?.data?.userDetails;
      const patientId = data?.data?.patientId;

      localStorage.setItem(
        "user",
        JSON.stringify({
          ...userDetails, // ✅ spread userId, userName, userType
          patientId, // ✅ add patientId
        })
      );

      setStep("details");
    }

    if (error) {
      toast.error(error);
    }
  }, [
    status,
    token,
    message,
    error,
    data?.data?.userDetails,
    data?.data?.patientId,
  ]);

  useEffect(() => {
    if (saveStatus) {
      toast.success(saveMessage);
      setPatientDetails({
        nationalId: "",
        name: "",
        dob: "",
        age: "",
        gender: "",
        maritalStatus: "",
        nationality: "",
        visaType: "",
        otherId: "",
        occupation: "",
        address: "",
        phoneNo: "",
        emailId: "",
      });

      if (dataSave?.data?.userDetails) {
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...dataSave.data.userDetails, // userId, userName, userType
            patientId: dataSave.data.patientId, // ✅ add patientId
          })
        );
      }

      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (storedUser?.userType?.toLowerCase() === "patient") {
        navigate("/patient-dashboard");
      }
    }

    if (errorSave) {
      toast.error(errorSave);
    }
  }, [saveStatus, saveMessage, errorSave, navigate, dataSave]);

  // Auto-calc age when DOB changes
  useEffect(() => {
    if (patientDetails.dob) {
      const birthDate = new Date(patientDetails.dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      setPatientDetails((prev) => ({ ...prev, age: age.toString() }));
    }
  }, [patientDetails.dob]);

  const [phonePlaceholder, setPhonePlaceholder] = useState("");

  const validatePassword = (password) => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^])[A-Za-z\d@$!%*?&#^]{8,}$/;
    return regex.test(password);
  };

  const validateName = (name) => {
    const regex = /^[A-Z][a-zA-Z\s]*$/;
    return regex.test(name);
  };

  const validateNationalId = (id) => {
    // Example: allow only alphanumeric, 6–12 characters
    const regex = /^[A-Za-z0-9]{6,12}$/;
    return regex.test(id);
  };

  const validateOtherId = (id) => {
    // Example: must start with a letter, alphanumeric, 4–10 chars
    const regex = /^[A-Za-z][A-Za-z0-9]{3,9}$/;
    return regex.test(id);
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePhone = (phone, code) => {
    let regex;
    switch (code) {
      case "+91": // India
        regex = /^[6-9]\d{9}$/;
        break;
      case "+1": // USA
        regex = /^[2-9]\d{9}$/;
        break;
      case "+971": // UAE
        regex = /^5\d{8}$/;
        break;
      case "+44": // UK
        regex = /^7\d{9}$/;
        break;
      default:
        regex = /^\d{7,15}$/;
    }
    return regex.test(phone);
  };

  const handleContinue = () => {
    if (!regUserName || !regPassword) {
      toast.warning("Please enter Username & Password");
      return;
    }
    if (!validatePassword(regPassword)) {
      toast.error(
        "Password must be at least 8 chars, include uppercase, lowercase, number & special character"
      );
      return;
    }

    dispatch(
      RegiterPatient({
        userName: regUserName.trim(),
        password: regPassword,
        deviceId,
      })
    );
  };

  const handleSave = () => {
    if (!validateName(patientDetails.name)) {
      toast.error("Name must start with a capital letter");
      return;
    }

    if (!validateNationalId(patientDetails.nationalId)) {
      toast.error("Invalid National ID (6–12 alphanumeric characters)");
      return;
    }

    if (!validateOtherId(patientDetails.otherId)) {
      toast.error(
        "Invalid Other ID (must start with a letter, 4–10 alphanumeric)"
      );
      return;
    }

    if (!validateEmail(patientDetails.emailId)) {
      toast.error("Invalid email address");
      return;
    }

    if (!validatePhone(patientDetails.phoneNo, countryCode)) {
      toast.error("Invalid phone number for selected nationality");
      return;
    }

    dispatch(savePatientDetails(patientDetails));
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        bgcolor: "#f4f7fb",
        overflow: "hidden",
      }}
    >
      {/* ===== LEFT PANEL ===== */}
      <Box
        sx={{
          flex: 1,
          background: "linear-gradient(135deg, #2563eb, #16a34a)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          color: "white",
          p: 6,
          textAlign: "center",
        }}
      >
        <img
          src="https://cdn-icons-png.flaticon.com/512/2966/2966484.png"
          alt="Telemedicine"
          style={{ width: "200px", marginBottom: "20px" }}
        />
        <Typography variant="h3" fontWeight="bold" sx={{ mb: 2 }}>
          Patient Registration
        </Typography>
        <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
          Register as a new patient to access telemedicine services.
        </Typography>

        <Box textAlign="left" sx={{ maxWidth: 400 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            ✅ Secure Account Setup
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            ✅ Easy Patient Onboarding
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            ✅ Access to Doctors & Prescriptions
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            ✅ Encrypted Medical Records
          </Typography>
        </Box>
      </Box>

      {/* ===== RIGHT PANEL ===== */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 4,
        }}
      >
        <Box
          sx={{
            width: 500,
            background: "white",
            borderRadius: 4,
            boxShadow: 6,
            p: 4,
          }}
        >
          {/* Title */}
          <Typography
            variant="h5"
            align="center"
            gutterBottom
            sx={{ fontWeight: "bold", color: "#16a34a", mb: 3 }}
          >
            {step === "account" ? "Create Account" : "Patient Details"}
          </Typography>

          {/* ===== Step 1: Account setup ===== */}
          {step === "account" && (
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Username *
              </Typography>
              <Input
                size="large"
                placeholder="Create Username"
                prefix={<UserOutlined />}
                value={regUserName}
                onChange={(e) => setRegUserName(e.target.value)}
              />

              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Password *
              </Typography>
              <Input.Password
                size="large"
                placeholder="Create Password"
                prefix={<LockOutlined />}
                value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)}
              />

              <Button
                type="primary"
                block
                size="large"
                onClick={handleContinue}
                loading={loading}
              >
                {loading ? "Loading..." : "Continue"}
              </Button>
              <Button type="link" block onClick={() => navigate("/")}>
                Back to Login
              </Button>
            </Box>
          )}

          {/* ===== Step 2: Patient Details ===== */}
          {step === "details" && (
            <Box display="flex" flexDirection="column" gap={2}>
              {/* National ID & Full Name */}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    National ID *
                  </Typography>
                  <Input
                    placeholder="Enter National ID"
                    prefix={<IdcardOutlined />}
                    value={patientDetails.nationalId}
                    onChange={(e) =>
                      setPatientDetails({
                        ...patientDetails,
                        nationalId: e.target.value,
                      })
                    }
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Full Name *
                  </Typography>
                  <Input
                    placeholder="Enter Full Name"
                    value={patientDetails.name}
                    onChange={(e) =>
                      setPatientDetails({
                        ...patientDetails,
                        name: e.target.value,
                      })
                    }
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Date of Birth *
                  </Typography>
                  <Input
                    type="date"
                    value={patientDetails.dob}
                    onChange={(e) =>
                      setPatientDetails({
                        ...patientDetails,
                        dob: e.target.value,
                      })
                    }
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Age *
                  </Typography>
                  <Input
                    placeholder="Enter Age"
                    value={patientDetails.age}
                    onChange={(e) =>
                      setPatientDetails({
                        ...patientDetails,
                        age: e.target.value,
                      })
                    }
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Gender *
                  </Typography>
                  <Select
                    placeholder="Select Gender"
                    value={patientDetails.gender}
                    onChange={(value) =>
                      setPatientDetails({ ...patientDetails, gender: value })
                    }
                    style={{ width: "100%" }}
                  >
                    {genderOptions.map((g) => (
                      <Option key={g} value={g}>
                        {g}
                      </Option>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Nationality *
                  </Typography>
                  <Select
                    placeholder="Select Nationality"
                    value={patientDetails.nationality}
                    onChange={(value) => {
                      const selected = nationalityOptions.find(
                        (n) => n.value === value
                      );
                      setPatientDetails({
                        ...patientDetails,
                        nationality: value,
                      });
                      setCountryCode(selected?.code || "");
                      setPhonePlaceholder(
                        phonePlaceholders[selected?.label] || ""
                      );
                    }}
                    style={{ width: "100%" }}
                  >
                    {nationalityOptions.map((n) => (
                      <Option key={n.value} value={n.value}>
                        {n.label}
                      </Option>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Visa Type *
                  </Typography>
                  <Select
                    placeholder="Select Visa Type"
                    value={patientDetails.visaType}
                    onChange={(value) =>
                      setPatientDetails({ ...patientDetails, visaType: value })
                    }
                    style={{ width: "100%" }}
                  >
                    {visaTypeOptions.map((v) => (
                      <Option key={v} value={v}>
                        {v}
                      </Option>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Marital Status *
                  </Typography>
                  <Select
                    placeholder="Enter Marital Status"
                    value={patientDetails.maritalStatus}
                    onChange={(value) =>
                      setPatientDetails({
                        ...patientDetails,
                        maritalStatus: value,
                      })
                    }
                    style={{ width: "100%" }}
                  >
                    {maritalStatusOptions.map((status) => (
                      <Option key={status} value={status}>
                        {status}
                      </Option>
                    ))}
                  </Select>
                </Box>
              </Box>

              {/* Marital Status */}

              {/* Other ID & Occupation */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Other ID *
                  </Typography>
                  <Input
                    placeholder="Enter Other ID"
                    value={patientDetails.otherId}
                    onChange={(e) =>
                      setPatientDetails({
                        ...patientDetails,
                        otherId: e.target.value,
                      })
                    }
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Occupation *
                  </Typography>
                  <Input
                    placeholder="Enter Occupation"
                    value={patientDetails.occupation}
                    onChange={(e) =>
                      setPatientDetails({
                        ...patientDetails,
                        occupation: e.target.value,
                      })
                    }
                  />
                </Box>
              </Box>

              {/* Address */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  Address *
                </Typography>
                <Input
                  placeholder="Enter Address"
                  prefix={<HomeOutlined />}
                  value={patientDetails.address}
                  onChange={(e) =>
                    setPatientDetails({
                      ...patientDetails,
                      address: e.target.value,
                    })
                  }
                />
              </Box>

              {/* Phone & Email */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Phone *
                  </Typography>
                  <Input
                    addonBefore={countryCode}
                    placeholder={phonePlaceholder}
                    prefix={<PhoneOutlined />}
                    value={patientDetails.phoneNo}
                    onChange={(e) =>
                      setPatientDetails({
                        ...patientDetails,
                        phoneNo: e.target.value,
                      })
                    }
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Email *
                  </Typography>
                  <Input
                    placeholder="Enter Email"
                    prefix={<MailOutlined />}
                    value={patientDetails.emailId}
                    onChange={(e) =>
                      setPatientDetails({
                        ...patientDetails,
                        emailId: e.target.value,
                      })
                    }
                  />
                </Box>
              </Box>

              {/* Buttons */}

              {loadingSave && <Button disabled>Loading...</Button>}
              {!loadingSave && (
                <Box display="flex" gap={2} mt={2}>
                  <Button block onClick={() => setStep("account")}>
                    Back
                  </Button>
                  <Button type="primary" block onClick={handleSave}>
                    Save
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default RegisterPatient;
